import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getSpaceType } from '@confluence/space-utils/entry-points/getSpaceType.ts';

import { shouldSampleEvent, CONTENT_RESOLVER_SAMPLE_RATE } from './ConnectAnalyticsSampling.ts';

function _isCacheable(extension) {
	const href = extension.url;
	return href !== undefined && href.indexOf('xdm_e=') === -1;
}

function _getModuleType(extension) {
	return extension.options ? extension.options.moduleType : undefined;
}

function _getModuleLocation(extension) {
	return extension.options ? extension.options.moduleLocation : undefined;
}

function _getPearApp(extension) {
	return extension.options && extension.options.pearApp === 'true';
}

function _getContentId(extension) {
	return extension.options && extension.options.productContext
		? extension.options.productContext['content.id']
		: undefined;
}

function _getContentType(extension) {
	return extension.options && extension.options.productContext
		? extension.options.productContext['content.type']
		: undefined;
}

function _getSpaceId(extension) {
	return extension.options && extension.options.productContext
		? extension.options.productContext['space.id']
		: undefined;
}

function _getSpaceKey(extension) {
	return extension.options && extension.options.productContext
		? extension.options.productContext['space.key']
		: undefined;
}

function pickAttributes(extension) {
	return {
		moduleType: _getModuleType(extension),
		iframeIsCacheable: _isCacheable(extension),
		moduleKey: extension.key,
		moduleLocation: _getModuleLocation(extension),
		pearApp: _getPearApp(extension),
	};
}

function buildAppAnalyticsEvent(activationId, extension) {
	const moduleType = _getModuleType(extension);
	const event: any = {
		action: 'invoked',
		actionSubject: 'connectAddon',
		actionSubjectId: extension.addon_key,
		source: moduleType,
		attributes: {
			activationId,
			appId: extension.addon_key,
			moduleType,
			moduleKey: extension.key,
		},
	};

	const spaceId = _getSpaceId(extension);
	if (spaceId) {
		event.attributes.spaceId = spaceId;
		event.attributes.spaceType = getSpaceType(_getSpaceKey(extension));
		event.containerType = 'space';
		event.containerId = spaceId;
		const contentId = _getContentId(extension);
		if (contentId) {
			event.objectType = _getContentType(extension);
			event.objectId = contentId;
		}
	}
	return event;
}

export const ConnectAnalyticsPublishSupport = new (class ConnectAnalyticsPublishSupport {
	async trackIframePerformanceTelemetry(extension, metrics) {
		const analyticsWebClient = await getAnalyticsWebClient();
		analyticsWebClient.sendOperationalEvent({
			source: extension.addon_key,
			action: 'iframeRendered',
			actionSubject: 'connectAddon',
			actionSubjectId: extension.addon_key,
			attributes: Object.assign(
				{
					domainLookupTime: metrics.domainLookupTime,
					connectionTime: metrics.connectionTime,
					decodedBodySize: metrics.decodedBodySize,
					domContentLoadedTime: metrics.domContentLoadedTime,
					fetchTime: metrics.fetchTime,
				},
				pickAttributes(extension),
			),
		});
	}

	async trackIframeLoaded(extension) {
		const analyticsWebClient = await getAnalyticsWebClient();
		analyticsWebClient.sendOperationalEvent({
			action: 'iframeLoaded',
			actionSubject: 'connectAddon',
			actionSubjectId: extension['addon_key'],
			attributes: pickAttributes(extension),
			source: extension.addon_key,
		});
	}

	async trackIframeTimeout(extension) {
		const analyticsWebClient = await getAnalyticsWebClient();
		analyticsWebClient.sendOperationalEvent({
			action: 'iframeTimeout',
			actionSubject: 'connectAddon',
			actionSubjectId: extension['addon_key'],
			attributes: pickAttributes(extension),
			source: extension.addon_key,
		});
	}

	async trackInvalidIframeTimeout(extension) {
		const analyticsWebClient = await getAnalyticsWebClient();
		analyticsWebClient.sendOperationalEvent({
			action: 'iframeInvalidTimeout',
			actionSubject: 'connectAddon',
			actionSubjectId: extension['addon_key'],
			attributes: pickAttributes(extension),
			source: extension.addon_key,
		});
	}

	async trackIframeViewed(extension) {
		const analyticsWebClient = await getAnalyticsWebClient();
		analyticsWebClient.sendOperationalEvent({
			action: 'iframeViewed',
			actionSubject: 'connectAddon',
			actionSubjectId: extension['addon_key'],
			attributes: pickAttributes(extension),
			source: extension.addon_key,
		});
	}

	async trackContentResolverEvent(eventData) {
		const analyticsWebClient = await getAnalyticsWebClient();
		const { success, extra } = eventData;
		const action = success ? 'contentResolverSucceeded' : 'contentResolverFailed';
		const { extension, attrs } = extra;
		const { moduleType, moduleKey, moduleLocation, pearApp } = pickAttributes(extension);

		const attributes = {
			...attrs,
			moduleType,
			moduleKey,
			moduleLocation,
			pearApp,
		};
		if (shouldSampleEvent(CONTENT_RESOLVER_SAMPLE_RATE)) {
			analyticsWebClient.sendOperationalEvent({
				action,
				actionSubject: 'connectAddon',
				actionSubjectId: extension.addon_key,
				attributes,
				source: extension.addon_key,
			});
		}
	}

	async sendAppAnalyticsEvent(activationId, extension) {
		const analyticsWebClient = await getAnalyticsWebClient();
		const event = buildAppAnalyticsEvent(activationId, extension);
		analyticsWebClient.sendTrackEvent(event);
	}

	async handleForwardedAnalyticsEvent(type, event) {
		const analyticsWebClient = await getAnalyticsWebClient();
		switch (type) {
			case 'ui':
				analyticsWebClient.sendUIEvent(event);
				break;
			case 'operational':
				analyticsWebClient.sendOperationalEvent(event);
				break;
			case 'track':
				analyticsWebClient.sendTrackEvent(event);
				break;
			case 'screen':
				analyticsWebClient.sendScreenEvent(event);
				break;
			default:
				// eslint-disable-next-line no-console
				console.error(`Unknown event type forwarded from ConnectHost: ${type}`);
		}
	}
})();
